/**
 * When a campaign goes out, and in whose clock.
 *
 * Two strategies, because there are two genuinely different questions and
 * collapsing them loses the one people actually ask for. "Send at nine on
 * Tuesday" means one instant for everybody. "Send at nine their time" means a
 * different instant per recipient, and a single timestamp column cannot hold
 * that no matter how it is interpreted.
 *
 * **Stored as one nullable jsonb column rather than four nullable columns.** A
 * campaign either has a schedule or does not, and the fields that go with
 * `recipient-local` are meaningless for `at-once`. Four columns would make
 * "scheduled at-once with a fallback timezone and no instant" a state the
 * database allows and nothing in the product means.
 *
 * **The conversion to each recipient's instant is not done here.** It is done
 * by Postgres, in the one statement that fills the queue, using `at time zone`
 * against its own IANA database. Doing it in TypeScript would mean loading
 * every contact to compute a timestamp for each — the thing the queue insert
 * exists to avoid — and would put a second daylight-saving implementation
 * beside the one the database already maintains.
 */
/** `YYYY-MM-DDTHH:mm`, and a real date rather than the 31st of February. */
const LOCAL_AT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
/**
 * The furthest ahead of UTC any civil timezone runs.
 *
 * Kiritimati is UTC+14, so nine in the morning there is the earliest nine in
 * the morning anywhere. A `recipient-local` campaign becomes due this long
 * before its wall clock reads the chosen time in UTC, because by then the
 * first recipient's moment may already have arrived. Everyone else's row sits
 * in the queue with its own later `scheduled_at` and is simply not claimed
 * until it is due — which is the behaviour the claim query already has.
 */
const EARLIEST_ZONE_HOURS = 14;
/**
 * The furthest behind UTC any civil timezone runs, for saying when a
 * recipient-local campaign will have finished.
 */
const LATEST_ZONE_HOURS = 12;
export function isValidLocalAt(localAt) {
    const parts = LOCAL_AT.exec(localAt);
    if (parts === null)
        return false;
    const [, year, month, day, hour, minute] = parts.map(Number);
    if (month < 1 || month > 12 || hour > 23 || minute > 59 || day < 1)
        return false;
    // Round-tripped rather than range-checked, so February and leap years are
    // the calendar's problem rather than this function's.
    const asUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
    return asUtc.getUTCMonth() === month - 1 && asUtc.getUTCDate() === day;
}
/**
 * Whether a timezone name is one this machine's calendar understands.
 *
 * Asked of the platform rather than matched against a list, because the list
 * changes — zones are added, renamed and abolished by politics — and a copy of
 * it here would be wrong within a year and wrong silently.
 */
export function isValidTimezone(zone) {
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * When the campaign should be taken off the shelf and put on the queue.
 *
 * For `at-once` that is the instant itself. For `recipient-local` it is the
 * chosen wall clock read in the earliest zone on earth, because from that
 * moment somebody's nine o'clock has arrived and the rows have to exist for
 * the queue to hold them.
 */
export function queueableFrom(schedule) {
    if (schedule.kind === 'at-once')
        return schedule.at;
    return new Date(wallClockUtc(schedule.localAt) - EARLIEST_ZONE_HOURS * 3_600_000);
}
/**
 * The last moment a recipient-local campaign can still be delivering.
 *
 * Shown to whoever scheduled it, because "nine their time" spans a day and a
 * bit, and somebody who expects a campaign to be finished by lunchtime should
 * find that out while they can still choose otherwise.
 */
export function finishedBy(schedule) {
    if (schedule.kind === 'at-once')
        return schedule.at;
    return new Date(wallClockUtc(schedule.localAt) + LATEST_ZONE_HOURS * 3_600_000);
}
/** Due means due: a schedule in the past is one that should already have run. */
export function isDue(schedule, now) {
    return queueableFrom(schedule).getTime() <= now.getTime();
}
/**
 * What is wrong with a schedule, or nothing.
 *
 * In the domain rather than in a Zod schema because two of the three rules are
 * about meaning rather than shape — a time in the past, and a zone this
 * platform cannot read — and the screen needs the same answer the server will
 * give it.
 */
export function problemsWithSchedule(schedule, now) {
    const problems = [];
    if (schedule.kind === 'at-once') {
        if (Number.isNaN(schedule.at.getTime())) {
            problems.push('That is not a date and time we can read.');
        }
        else if (schedule.at.getTime() <= now.getTime()) {
            problems.push('That moment has already passed. Pick one in the future, or send it now.');
        }
        return problems;
    }
    if (!isValidLocalAt(schedule.localAt)) {
        problems.push('That is not a date and time we can read.');
    }
    else if (queueableFrom(schedule).getTime() <= now.getTime()) {
        // Measured from the earliest zone, because that is the first moment this
        // campaign would have had to start sending.
        problems.push('That time has already passed somewhere in the world. Pick a later one, or send it now.');
    }
    if (!isValidTimezone(schedule.fallbackTimezone)) {
        problems.push(`We do not recognise the timezone “${schedule.fallbackTimezone}”.`);
    }
    return problems;
}
/** A wall clock read as though it were UTC, which is the arithmetic anchor. */
function wallClockUtc(localAt) {
    return Date.parse(`${localAt}:00Z`);
}
//# sourceMappingURL=campaignSchedule.js.map